"use client";

import { LoginDrawer } from "@/app/(site)/(main)/components/LoginDrawer";
import { ROUTES } from "@/constants/routes";
import { useCurrentUser } from "@/hooks/user";
import { useProfileImageUrl } from "@/hooks/user/useProfileImageUrl";
import { ButtonV2, Typo, useDrawer } from "@repo/ui/components";
import { EllipsisVerticalIcon, SettingsIcon } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import type { ReactNode } from "react";

export { SiteHeaderView } from "./SiteHeaderView";
export type { SiteHeaderViewProps } from "./SiteHeaderView";
import { SiteHeaderView } from "./SiteHeaderView";
import type { SiteHeaderViewProps } from "./SiteHeaderView";

/* ─── Presenter (hooks → View) ────────────────────────── */

interface SiteHeaderPresenterProps {
  showBack?: boolean;
  showHomeIcon?: boolean;
  title?: string;
  showSearch?: boolean;
  showProfile?: boolean;
  fallbackRight?: ReactNode;
  rightExtra?: ReactNode;
}

export function SiteHeaderPresenter({
  showBack = false,
  showHomeIcon = false,
  title,
  showSearch,
  showProfile,
  fallbackRight,
  rightExtra,
}: SiteHeaderPresenterProps) {
  const router = useRouter();
  const { data: currentUser, isLoading, isError } = useCurrentUser();
  const profileImageUrl = useProfileImageUrl();

  const showSkeleton = isLoading && !isError;

  return (
    <SiteHeaderView
      showBack={showBack}
      showHomeIcon={showHomeIcon}
      title={title}
      showSearch={showSearch}
      showProfile={showProfile}
      rightExtra={rightExtra}
      fallbackRight={
        showSkeleton ? (
          <div className="size-9 animate-pulse rounded-full bg-zinc-200" />
        ) : (
          fallbackRight
        )
      }
      user={currentUser}
      profileImageUrl={profileImageUrl}
      onBack={() => router.back()}
      onSearchClick={() => router.push(ROUTES.SEARCH)}
      onProfileClick={() => router.push(ROUTES.ME)}
    />
  );
}

/* ─── SiteHeader (main layout entry) ─────────────────── */

const HIDDEN_PATHS = ["/editor"];

export function SiteHeader() {
  const pathname = usePathname();

  if (HIDDEN_PATHS.some(path => pathname.startsWith(path))) {
    return null;
  }

  return <SiteHeaderPresenter {...defaultHeaderConfig()} />;
}

/* ─── Config Presets ──────────────────────────────────── */

export function defaultHeaderConfig(): Partial<SiteHeaderViewProps> {
  return { fallbackRight: <HeaderLoginDrawer /> };
}

export function meRootHeaderConfig(showBack?: boolean): Partial<SiteHeaderViewProps> {
  return {
    showBack,
    showHomeIcon: true,
    showSearch: false,
    showProfile: false,
    rightExtra: <SettingsLink />,
  };
}

export function meSubPageHeaderConfig(
  title: string,
  showBack?: boolean,
): Partial<SiteHeaderViewProps> {
  return { showBack, title, showSearch: false, showProfile: false };
}

export function missionIntroHeaderConfig(
  isOwner: boolean,
  onMenuClick?: () => void,
): Partial<SiteHeaderViewProps> {
  return {
    showHomeIcon: isOwner,
    fallbackRight: <HeaderLoginDrawer />,
    rightExtra: isOwner && onMenuClick ? <OwnerMenuButton onClick={onMenuClick} /> : undefined,
  };
}

export function missionIntroScrolledHeaderConfig(
  title?: string,
  onBack?: () => void,
): Partial<SiteHeaderViewProps> {
  return { showBack: true, title, showSearch: false, showProfile: false, onBack };
}

/* ─── Slot Components ─────────────────────────────────── */

function LoginDrawerTrigger() {
  const { open } = useDrawer();

  return (
    <ButtonV2
      variant="tertiary"
      size="medium"
      onClick={open}
      className="text-sub border border-default"
    >
      <Typo.ButtonText size="medium">로그인/가입</Typo.ButtonText>
    </ButtonV2>
  );
}

function HeaderLoginDrawer() {
  return (
    <div className="flex items-center justify-center h-12 px-3">
      <LoginDrawer>
        <LoginDrawerTrigger />
      </LoginDrawer>
    </div>
  );
}

function OwnerMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-center justify-center size-12">
      <button
        type="button"
        onClick={onClick}
        className="flex shrink-0 items-center justify-center p-1"
      >
        <EllipsisVerticalIcon className="size-5" />
      </button>
    </div>
  );
}

function SettingsLink() {
  return (
    <Link href={ROUTES.ME_ACCOUNT} className="flex size-12 items-center justify-center">
      <SettingsIcon className="size-6" />
    </Link>
  );
}
